import { useState } from 'react';
import { Copy, RefreshCw, X, UserPlus, Send } from 'lucide-react';
import { palette } from '../../styles/tokens';
import { EmptyState } from '../common/EmptyState';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { useTeamInvites } from '../../hooks/useTeamInvites';
import { deriveInviteStatus } from '../../types/teamInvites';
import type { TeamInvite, InviteRole } from '../../types/teamInvites';

const TH: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, color: palette.g400, textTransform: 'uppercase',
  letterSpacing: '0.06em', padding: '10px 12px', textAlign: 'left',
  borderBottom: `1px solid ${palette.g150}`, whiteSpace: 'nowrap',
};
const TD: React.CSSProperties = {
  fontSize: 13, color: palette.g700, padding: '10px 12px',
  borderBottom: `1px solid ${palette.g100}`, whiteSpace: 'nowrap',
};
const INPUT: React.CSSProperties = {
  padding: '8px 12px', borderRadius: 6, border: `1px solid ${palette.g200}`,
  fontSize: 13, color: palette.g800, fontFamily: 'inherit', outline: 'none',
};
const BTN: React.CSSProperties = {
  padding: '4px 8px', borderRadius: 4, border: 'none', cursor: 'pointer',
  fontSize: 11, fontWeight: 500, fontFamily: 'inherit', display: 'inline-flex',
  alignItems: 'center', gap: 3,
};

const STATUS_COLORS: Record<string, { bg: string; fg: string }> = {
  pending: { bg: palette.amberWash, fg: palette.amber },
  expired: { bg: palette.g100, fg: palette.g400 },
  claimed: { bg: palette.greenWash, fg: palette.green },
};

const ROLE_OPTIONS: { label: string; value: InviteRole }[] = [
  { label: 'Employee', value: 'employee' },
  { label: 'Contractor', value: 'contractor' },
  { label: 'Operator', value: 'operator' },
];

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function buildShareUrl(code: string): string {
  return `https://crewbit.app/invite/${code}`;
}

interface Props {
  businessId: string | undefined;
  onToast?: (msg: string, type: 'success' | 'error') => void;
}

export function TeamInvitesTab({ businessId, onToast }: Props) {
  const { invites, loading, createInvite, revokeInvite, resendInvite } = useTeamInvites(businessId);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [role, setRole] = useState<InviteRole>('employee');
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<TeamInvite | null>(null);
  const [revoking, setRevoking] = useState(false);

  // Filter: show pending + expired only (not claimed, not revoked)
  const visible = invites.filter((inv) => {
    const s = deriveInviteStatus(inv);
    return s === 'pending' || s === 'expired';
  });

  const handleCreate = async () => {
    if (!name.trim()) return;
    setCreating(true);
    const result = await createInvite(name.trim(), role);
    setCreating(false);
    if (result.success && result.code) {
      const url = buildShareUrl(result.code);
      void navigator.clipboard.writeText(url);
      onToast?.(`Invite created for ${name.trim()} — link copied!`, 'success');
      setShowCreate(false);
      setName('');
      setRole('employee');
    } else {
      onToast?.('Failed to create invite.', 'error');
    }
  };

  const handleCopy = (code: string) => {
    void navigator.clipboard.writeText(buildShareUrl(code));
    setCopied(code);
    onToast?.('Invite link copied!', 'success');
    setTimeout(() => setCopied(null), 2000);
  };

  const handleResend = async (invite: TeamInvite) => {
    const result = await resendInvite(invite);
    if (result.success && result.code) {
      void navigator.clipboard.writeText(buildShareUrl(result.code));
      onToast?.(`New invite sent for ${invite.invited_name} — link copied!`, 'success');
    } else {
      onToast?.('Failed to resend invite.', 'error');
    }
  };

  const handleRevoke = (invite: TeamInvite) => {
    setConfirmRevoke(invite);
  };

  const performRevoke = async () => {
    if (!confirmRevoke) return;
    setRevoking(true);
    try {
      const result = await revokeInvite(confirmRevoke.id);
      if (result.success) onToast?.('Invite revoked.', 'success');
      else onToast?.('Failed to revoke.', 'error');
      setConfirmRevoke(null);
    } finally {
      setRevoking(false);
    }
  };

  if (loading) return <div style={{ padding: 20, textAlign: 'center', color: palette.g400, fontSize: 13 }}>Loading...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: palette.g500 }}>
          {visible.length} pending invite{visible.length !== 1 ? 's' : ''}
        </div>
        <button onClick={() => setShowCreate(!showCreate)} style={{
          ...BTN, padding: '6px 12px', fontSize: 12.5, fontWeight: 600,
          background: palette.accent, color: palette.white,
        }}><UserPlus size={14} /> Invite Member</button>
      </div>

      {showCreate && (
        <div style={{
          padding: 16, background: palette.g50, borderRadius: 8,
          border: `1px solid ${palette.g150}`, marginBottom: 16,
          display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap',
        }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 500, color: palette.g500, display: 'block', marginBottom: 4 }}>Name</label>
            <input
              style={INPUT}
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Team member's name"
            />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 500, color: palette.g500, display: 'block', marginBottom: 4 }}>Role</label>
            <select
              style={{ ...INPUT, minWidth: 120 }}
              value={role}
              onChange={e => setRole(e.target.value as InviteRole)}
            >
              {ROLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <button
            onClick={handleCreate}
            disabled={creating || !name.trim()}
            style={{
              ...BTN, padding: '8px 16px', fontSize: 12.5, fontWeight: 600,
              background: creating || !name.trim() ? palette.g300 : palette.accent,
              color: palette.white,
            }}
          >
            {creating ? 'Creating...' : 'Create & Copy Link'}
          </button>
          <button
            onClick={() => { setShowCreate(false); setName(''); setRole('employee'); }}
            style={{ ...BTN, padding: '8px 12px', background: palette.white, border: `1px solid ${palette.g200}`, color: palette.g600 }}
          >
            Cancel
          </button>
        </div>
      )}

      {visible.length === 0 ? (
        <EmptyState
          icon={<Send size={22} />}
          title="No pending invites"
          subtitle="Invite someone to join your team on CrewBit."
          actionLabel="Invite Member"
          onAction={() => setShowCreate(true)}
        />
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={TH}>Name</th>
              <th style={TH}>Role</th>
              <th style={TH}>Code</th>
              <th style={TH}>Status</th>
              <th style={TH}>Created</th>
              <th style={TH}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(inv => {
              const status = deriveInviteStatus(inv);
              const sc = STATUS_COLORS[status] ?? STATUS_COLORS.expired;
              return (
                <tr
                  key={inv.id}
                  style={{ transition: 'background 150ms ease' }}
                  onMouseEnter={e => (e.currentTarget.style.background = palette.g50)}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <td style={{ ...TD, fontWeight: 500 }}>{inv.invited_name}</td>
                  <td style={TD}>{inv.role.charAt(0).toUpperCase() + inv.role.slice(1)}</td>
                  <td style={{ ...TD, fontFamily: 'monospace', fontWeight: 600, letterSpacing: '0.05em' }}>{inv.code}</td>
                  <td style={TD}>
                    <span style={{
                      padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500,
                      background: sc.bg, color: sc.fg,
                    }}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </span>
                  </td>
                  <td style={TD}>{relativeTime(inv.created_at)}</td>
                  <td style={TD}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button
                        onClick={() => handleCopy(inv.code)}
                        style={{ ...BTN, background: palette.g100, color: copied === inv.code ? palette.green : palette.g600 }}
                      >
                        <Copy size={12} /> {copied === inv.code ? 'Copied' : 'Copy Link'}
                      </button>
                      {status === 'pending' && (
                        <button onClick={() => handleResend(inv)} style={{ ...BTN, background: palette.amberWash, color: palette.amber }}>
                          <RefreshCw size={12} /> Resend
                        </button>
                      )}
                      <button onClick={() => handleRevoke(inv)} style={{ ...BTN, background: palette.redWash, color: palette.red }}>
                        <X size={12} /> Revoke
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <ConfirmDialog
        isOpen={!!confirmRevoke}
        title={`Revoke invite for ${confirmRevoke?.invited_name ?? ''}?`}
        message="The invite code will stop working immediately. They won't be able to join with this code. You can send a fresh invite later."
        confirmLabel="Revoke invite"
        confirmVariant="danger"
        loading={revoking}
        onClose={() => { if (!revoking) setConfirmRevoke(null); }}
        onConfirm={performRevoke}
      />
    </div>
  );
}
